import React, { useEffect, useState, useRef } from 'react';
import { Chart } from 'react-chartjs-2';
import { getMaterialUrl } from '../../config/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Scale,
  CoreScaleOptions,
  Tick,
  LineController,
  BarController,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  LineController,
  BarController,
  Title,
  Tooltip,
  Legend
);

interface PriceData {
  date: string;
  [key: string]: string;
}

interface DinamicProps {
  materialType: 'pe' | 'ppr' | 'pvh';
}

const parsePrice = (priceStr: any): number | null => {
  if (priceStr === null || priceStr === undefined || priceStr === 'N/A') return null;

  const strValue = String(priceStr);
  if (strValue === 'N/A') return null;

  const cleanStr = strValue.replace(/\s+/g, '');
  if (cleanStr.includes('-')) {
    const [min, max] = cleanStr.split('-').map(Number);
    return Math.max(min, max);
  }
  return Number(cleanStr);
};

const Dinamic: React.FC<DinamicProps> = ({ materialType }) => {
  const [priceData, setPriceData] = useState<PriceData[]>([]);
  const [selectedRange, setSelectedRange] = useState<[number, number]>([0, 1]);
  const [activeHandle, setActiveHandle] = useState<'left' | 'right' | null>(null);
  const [showAllData, setShowAllData] = useState(true);
  const [datasetVisibility, setDatasetVisibility] = useState<Record<string, boolean>>({});
  const timelineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Ответ по прошлой вкладке может прийти позже текущего и затереть график
    let isStale = false;

    const fetchData = async () => {
      try {
        const access = localStorage.getItem('access');
        const response = await fetch(getMaterialUrl(materialType), {
          headers: access ? { 'Authorization': `Bearer ${access}` } : {}
        });
        const data = await response.json();
        if (isStale) return;
        setPriceData(data);

        console.log('Data range:', {
          firstDate: data[0]?.date,
          lastDate: data[data.length - 1]?.date,
          totalPoints: data.length
        });

        const years = new Set(data.map((item: PriceData) => new Date(item.date).getFullYear()));
        console.log('Available years:', Array.from(years).sort());

        const initialVisibility: Record<string, boolean> = {};
        Object.keys(data[0] || {}).forEach(key => {
          if (key !== 'date' && key !== 'index') {
            initialVisibility[key] = true;
          }
        });
        setDatasetVisibility(initialVisibility);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();

    return () => {
      isStale = true;
    };
  }, [materialType]);

  const calculateTrend = (data: PriceData) => {
    const values = Object.entries(data)
      .filter(([key]) => key !== 'date' && key !== 'index')
      .map(([_, value]) => parsePrice(value))
      .filter((value): value is number => value !== null);

    return values.length > 0 ? Math.max(...values) : null;
  };

  const handleMouseDown = (e: React.MouseEvent, handle: 'left' | 'right') => {
    setActiveHandle(handle);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!activeHandle || !timelineRef.current) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const position = Math.max(0, Math.min(1, x / rect.width));

    if (activeHandle === 'left') {
      setSelectedRange([position, Math.max(position, selectedRange[1])]);
    } else {
      setSelectedRange([Math.min(position, selectedRange[0]), position]);
    }
  };

  const handleMouseUp = () => {
    setActiveHandle(null);
  };

  const filteredData = priceData.slice(
    Math.floor(selectedRange[0] * priceData.length),
    Math.ceil(selectedRange[1] * priceData.length)
  );

  const toggleDatasetVisibility = (datasetKey: string) => {
    setDatasetVisibility(prev => ({
      ...prev,
      [datasetKey]: !prev[datasetKey]
    }));
  };

  const toggleAllDatasets = () => {
    const newShowAllData = !showAllData;
    setShowAllData(newShowAllData);
    const newVisibility: Record<string, boolean> = {};
    Object.keys(datasetVisibility).forEach(key => {
      newVisibility[key] = newShowAllData;
    });
    setDatasetVisibility(newVisibility);
  };

  const chartData = {
    labels: filteredData.map(item => new Date(item.date).toLocaleDateString()),
    datasets: [
      {
        label: 'Trend Line',
        data: filteredData.map(calculateTrend),
        borderColor: 'rgb(255, 0, 128)',
        backgroundColor: 'rgba(255, 0, 128, 0.5)',
        type: 'line' as const,
        yAxisID: 'y',
        borderWidth: 2,
        tension: 0.1,
        spanGaps: true,
        pointRadius: 4,
        pointHoverRadius: 6,
        fill: false,
        datalabels: {
          display: false
        },
        hidden: !showAllData
      },
      ...Object.keys(priceData[0] || {})
        .filter(key => key !== 'date' && key !== 'index')
        .map((key, index) => {
          const colors = [
            'rgba(255, 99, 132, 0.8)',
            'rgba(54, 162, 235, 0.8)',
            'rgba(255, 206, 86, 0.8)',
            'rgba(75, 192, 192, 0.8)',
            'rgba(153, 102, 255, 0.8)',
            'rgba(255, 159, 64, 0.8)',
            'rgba(0, 255, 0, 0.8)',
            'rgba(255, 0, 255, 0.8)',
          ];
          return {
            label: key,
            data: filteredData.map(item => parsePrice(item[key])),
            backgroundColor: colors[index % colors.length],
            type: 'bar' as const,
            yAxisID: 'y',
            datalabels: {
              display: false
            },
            hidden: !datasetVisibility[key]
          };
        }),
    ],
  };

  const options = {
    responsive: true,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    scales: {
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        min: 90000,
        max: 270000,
        ticks: {
          stepSize: 30000,
          callback: function (this: Scale<CoreScaleOptions>, tickValue: string | number) {
            return Number(tickValue).toLocaleString() + ' ₽';
          }
        },
      },
      x: {
        ticks: {
          callback: function (value: any, index: number) {
            const date = new Date(filteredData[index]?.date);
            const year = date.getFullYear();
            const month = date.getMonth() + 1;

            const dateRange = new Date(filteredData[filteredData.length - 1]?.date).getTime() -
              new Date(filteredData[0]?.date).getTime();
            const isLessThanYear = dateRange < 365 * 24 * 60 * 60 * 1000;

            if (isLessThanYear) {
              if (index === 0 || new Date(filteredData[index - 1]?.date).getMonth() !== date.getMonth()) {
                return `${month}.${year}`;
              }
            } else {
              if (index === 0 || new Date(filteredData[index - 1]?.date).getFullYear() !== year) {
                return year;
              }
            }
            return '';
          },
          min: 2020,
          max: 2024,
          autoSkip: false
        }
      }
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const label = context.dataset.label || '';
            const value = context.parsed?.y;
            if (value === null || value === undefined) {
              return `${label}: N/A`;
            }
            return `${label}: ${value.toLocaleString()} ₽`;
          }
        }
      },
      legend: {
        onClick: (e: any, legendItem: any) => {
          const datasetKey = legendItem.text;
          toggleDatasetVisibility(datasetKey);
        }
      }
    }
  };

  return (
    materialType === 'pe' ? null : (
      <div className="chart-container line-chart-container">
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '10px',
          gap: '10px'
        }}>
          <button
            onClick={toggleAllDatasets}
            style={{
              padding: '8px 16px',
              backgroundColor: showAllData ? '#f44336' : '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            {showAllData ? 'Скрыть все' : 'Показать все'}
          </button>
        </div>
        <div style={{ flex: 1, position: 'relative' }}>
          <Chart type='bar' data={chartData} options={options} />
        </div>
        <div
          ref={timelineRef}
          style={{
            backgroundColor: '#f0f0f0',
            position: 'relative',
            height: '30px',
          }}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div
            style={{
              position: 'absolute',
              left: `${selectedRange[0] * 100}%`,
              right: `${(1 - selectedRange[1]) * 100}%`,
              top: 0,
              bottom: 0,
              backgroundColor: 'rgba(75, 192, 192, 0.3)',
              border: '2px solid rgb(75, 192, 192)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: `${selectedRange[0] * 100}%`,
              top: 0,
              bottom: 0,
              width: '10px',
              backgroundColor: 'rgb(75, 192, 192)',
              cursor: 'ew-resize',
            }}
            onMouseDown={(e) => handleMouseDown(e, 'left')}
          />
          <div
            style={{
              position: 'absolute',
              right: `${(1 - selectedRange[1]) * 100}%`,
              top: 0,
              bottom: 0,
              width: '10px',
              backgroundColor: 'rgb(75, 192, 192)',
              cursor: 'ew-resize',
            }}
            onMouseDown={(e) => handleMouseDown(e, 'right')}
          />
        </div>
      </div>
    )
  );
};

export default Dinamic;
